import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type Session } from "./api/client";
import { useLive } from "./api/useLive";
import { LoginScreen } from "./components/LoginScreen";
import { MeterDetail } from "./components/MeterDetail";
import { MeterList } from "./components/MeterList";
import { LinkBadge } from "./components/StatusBadge";

type Theme = "system" | "light" | "dark";

// Состав и порядок дашборда. Бэкенд отдаёт счётчики по алфавиту имён, а на
// экране первыми должны стоять вводы — независимо от того, как они названы.
const HIDDEN = ["orno-01", "trans-01-5", "trans-01-6"];
const FIRST = ["trans-01-30", "trans-01-1"];

function rank(slug: string): number {
  const i = FIRST.indexOf(slug);
  return i === -1 ? FIRST.length : i;
}

/**
 * Оболочка приложения: сначала выясняем, нужен ли вход.
 *
 * Дашборд не монтируется, пока нет доступа, — иначе он открыл бы WebSocket,
 * который сервер всё равно отклонит, и пользователь увидел бы «нет связи»
 * вместо формы входа.
 */
export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setSession(await api.session());
    } catch {
      // Сети нет: не выбрасываем на экран входа, иначе офлайн-режим с
      // сохранёнными показаниями станет недоступен. Дашборд сам покажет,
      // что связи нет.
      setSession(null);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Сессия живёт месяц, но может кончиться при открытой вкладке или быть
    // сброшенной сменой пароля. Перепроверяем при возвращении на вкладку —
    // так вход появится сам, без загадочного «нет связи».
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  if (!checked) return null; // мгновение до ответа: мигать формой не нужно

  if (session?.authRequired && !session.authenticated) {
    return <LoginScreen onSuccess={() => void refresh()} />;
  }

  return <Dashboard onLogout={() => void refresh()} authRequired={session?.authRequired ?? false} />;
}

function Dashboard({ onLogout, authRequired }: { onLogout: () => void; authRequired: boolean }) {
  const { meters: allMeters, status, lastReading, fromCache } = useLive();
  // Скрытые точки убираем до всех расчётов, иначе они попадут в счётчик
  // «на связи N из M» и в отметку свежести данных.
  const meters = useMemo(
    () =>
      allMeters
        .filter((m) => !HIDDEN.includes(m.slug))
        .sort((a, b) => rank(a.slug) - rank(b.slug)),
    [allMeters],
  );
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem("theme") as Theme) ?? "system");

  // Первый пришедший счётчик выбираем автоматически, чтобы экран не был пустым.
  useEffect(() => {
    if (selectedId === null && meters.length) setSelectedId(meters[0].id);
  }, [meters, selectedId]);

  useEffect(() => {
    if (theme === "system") document.documentElement.removeAttribute("data-theme");
    else document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const selected = meters.find((m) => m.id === selectedId) ?? null;
  const online = meters.filter((m) => m.state.online).length;

  // Момент самого свежего замера среди счётчиков — им подписываем офлайн-режим,
  // чтобы кэшированные значения не приняли за текущие.
  const newestTs = meters.reduce<number>((max, m) => {
    const t = m.latestTs ? new Date(m.latestTs).getTime() : 0;
    return t > max ? t : max;
  }, 0);
  const offline = status === "offline" || (fromCache && meters.length > 0 && status !== "online");

  return (
    <div className="app">
      <header className="topbar">
        <h1>Учёт электроэнергии</h1>
        <span className="badge">
          на связи {online} из {meters.length}
        </span>
        <div className="spacer" />
        <LinkBadge status={status} />
        <button
          type="button"
          className="icon-button"
          onClick={() => setTheme(nextTheme(theme))}
          title="Переключить тему"
        >
          {{ system: "тема: авто", light: "тема: светлая", dark: "тема: тёмная" }[theme]}
        </button>
        {authRequired && (
          <button
            type="button"
            className="icon-button"
            onClick={async () => {
              await api.logout().catch(() => undefined);
              onLogout();
            }}
            title="Выйти из дашборда"
          >
            выйти
          </button>
        )}
      </header>

      {offline && meters.length > 0 && (
        <div style={{ padding: "12px 20px 0" }}>
          <div className="error-banner">
            Нет связи с сервером — показаны сохранённые значения
            {newestTs > 0 && <> от {new Date(newestTs).toLocaleString("ru-RU")}</>}.
            Переподключение выполняется автоматически.
          </div>
        </div>
      )}

      <div className="layout">
        <aside>
          <MeterList meters={meters} selectedId={selectedId} onSelect={setSelectedId} />
        </aside>

        <main>
          {selected ? (
            <MeterDetail meter={selected} lastReading={lastReading} offline={offline} />
          ) : (
            <div className="panel empty-state">
              {status === "connecting" ? "Подключаемся к серверу…" : "Выберите счётчик в списке слева"}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function nextTheme(t: Theme): Theme {
  return t === "system" ? "light" : t === "light" ? "dark" : "system";
}
